import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, TWITTER_HANDLE } from "../config";

/**
 * Met à jour <title>, meta description, Open Graph, Twitter, canonical et JSON-LD pour une page,
 * puis restaure l'état précédent quand la page est démontée (navigation SPA).
 * Sans dépendance (pas de react-helmet), volontairement simple.
 */

const head = () => (typeof document !== "undefined" ? document.head : null);

/**
 * Récupère la balise correspondant au sélecteur, ou la crée.
 * Les balises créées par le hook sont empilées dans `created` : elles seront
 * retirées du <head> au démontage, pour ne rien laisser traîner derrière soi.
 */
const ensure = (selector, create, created) => {
  const h = head();
  if (!h) return null;
  let el = h.querySelector(selector);
  if (!el) {
    el = create();
    h.appendChild(el);
    if (created) created.push(el);
  }
  return el;
};

const metaByName = (name, created) =>
  ensure(
    `meta[name="${name}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    },
    created
  );

const metaByProperty = (property, created) =>
  ensure(
    `meta[property="${property}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute("property", property);
      return m;
    },
    created
  );

const canonicalLink = created =>
  ensure(
    'link[rel="canonical"]',
    () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    },
    created
  );

const jsonLdScript = created =>
  ensure(
    'script[type="application/ld+json"][data-page-meta]',
    () => {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-page-meta", "true");
      return s;
    },
    created
  );

const absoluteUrl = path => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

/** Types schema.org considérés comme « un article » pour en déduire dates et auteur. */
const ARTICLE_TYPES = ["Article", "BlogPosting", "NewsArticle", "TechArticle", "ReportageNewsArticle"];

/** Cherche le nœud d'article dans le JSON-LD fourni (objet unique ou tableau). */
const findArticleNode = jsonLd => {
  const nodes = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return nodes.find(node => node && typeof node === "object" && ARTICLE_TYPES.indexOf(node["@type"]) !== -1) || null;
};

/** Nom lisible de l'auteur d'un nœud JSON-LD (chaîne, objet Person ou tableau). */
const authorNameOf = node => {
  const author = node && node.author;
  if (!author) return "";
  if (typeof author === "string") return author;
  if (Array.isArray(author)) {
    return author.map(a => (typeof a === "string" ? a : a && a.name)).filter(Boolean).join(", ");
  }
  return author.name || "";
};

/**
 * @param {object} meta
 * @param {string} meta.title         Titre de la page (le nom du site est ajouté automatiquement).
 * @param {string} meta.description   Description (≈ 150 caractères).
 * @param {string} meta.path          Chemin canonique, ex. "/blog/mon-article".
 * @param {string} [meta.image]       Image OG (URL absolue ou chemin).
 * @param {string} [meta.type]        "website" | "article".
 * @param {object|object[]} [meta.jsonLd]  Données structurées schema.org.
 * @param {boolean} [meta.noindex]    Empêche l'indexation (résultats de recherche, etc.).
 * @param {boolean} [meta.rawTitle]   Si vrai, le titre est utilisé tel quel.
 * @param {string} [meta.publishedTime] Date de publication ISO (article) ; sinon déduite du JSON-LD.
 * @param {string} [meta.modifiedTime]  Date de mise à jour ISO (article) ; sinon déduite du JSON-LD.
 * @param {string} [meta.author]        Auteur de la page ; sinon déduit du JSON-LD, puis le site.
 */
export default function usePageMeta({
  title,
  description,
  path,
  image,
  type = "website",
  jsonLd,
  noindex = false,
  rawTitle = false,
  publishedTime,
  modifiedTime,
  author,
}) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  // Dates et auteur : fournis explicitement, sinon repris du nœud d'article du JSON-LD
  // (les pages d'articles existantes le renseignent déjà). Valeurs primitives : utilisables en dépendances.
  const articleNode = type === "article" ? findArticleNode(jsonLd) : null;
  const published = publishedTime || (articleNode && articleNode.datePublished) || "";
  const modified = modifiedTime || (articleNode && articleNode.dateModified) || published;
  const authorName = author || (articleNode && authorNameOf(articleNode)) || SITE_NAME;

  useEffect(() => {
    if (!head()) return undefined;

    const fullTitle = rawTitle || !title ? title || SITE_NAME : `${title} | ${SITE_NAME}`;
    const url = absoluteUrl(path);
    const ogImage = absoluteUrl(image || DEFAULT_OG_IMAGE);

    const created = [];
    const previous = { title: document.title, attrs: [] };
    const set = (el, attr, value) => {
      if (!el) return;
      previous.attrs.push({ el, attr, value: el.getAttribute(attr) });
      el.setAttribute(attr, value);
    };

    document.title = fullTitle;
    set(metaByName("description", created), "content", description || "");
    set(metaByName("author", created), "content", authorName);
    set(metaByProperty("og:site_name", created), "content", SITE_NAME);
    set(metaByProperty("og:title", created), "content", fullTitle);
    set(metaByProperty("og:description", created), "content", description || "");
    set(metaByProperty("og:url", created), "content", url);
    set(metaByProperty("og:type", created), "content", type);
    set(metaByProperty("og:image", created), "content", ogImage);
    set(metaByProperty("og:locale", created), "content", "fr_FR");
    set(metaByName("twitter:card", created), "content", "summary_large_image");
    set(metaByName("twitter:title", created), "content", fullTitle);
    set(metaByName("twitter:description", created), "content", description || "");
    set(metaByName("twitter:image", created), "content", ogImage);
    if (TWITTER_HANDLE) {
      set(metaByName("twitter:site", created), "content", TWITTER_HANDLE);
      set(metaByName("twitter:creator", created), "content", TWITTER_HANDLE);
    }
    // Balises propres aux articles : Facebook/LinkedIn et les agrégateurs s'en servent pour dater le contenu.
    if (type === "article") {
      if (published) set(metaByProperty("article:published_time", created), "content", published);
      if (modified) set(metaByProperty("article:modified_time", created), "content", modified);
      if (authorName) set(metaByProperty("article:author", created), "content", authorName);
    }
    set(canonicalLink(created), "href", url);
    if (noindex) set(metaByName("robots", created), "content", "noindex, follow");

    const script = jsonLdString ? jsonLdScript(created) : null;
    if (script) script.textContent = jsonLdString;

    return () => {
      document.title = previous.title;
      previous.attrs.forEach(({ el, attr, value }) => {
        // Les balises créées par ce hook sont retirées entièrement juste après : inutile de les restaurer.
        if (created.indexOf(el) !== -1) return;
        if (value == null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      });
      created.forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      // Filet de sécurité : balises déjà présentes dans le document et laissées vides.
      ["robots"].forEach(name => {
        const el = head().querySelector(`meta[name="${name}"]`);
        if (el && !el.getAttribute("content")) el.remove();
      });
      const ld = head().querySelector('script[type="application/ld+json"][data-page-meta]');
      if (ld) ld.remove();
      const canonical = head().querySelector('link[rel="canonical"]');
      if (canonical && !canonical.getAttribute("href")) canonical.remove();
    };
  }, [title, description, path, image, type, jsonLdString, noindex, rawTitle, published, modified, authorName]);
}
